import {
  PublicClientApplication,
  InteractionRequiredAuthError,
  EventMessage,
  EventMessageUtils,
  EventType,
  EventError,
  InteractionStatus,
  AccountInfo,
  AuthenticationResult,
  SilentRequest
} from '@azure/msal-browser'
import { msalConfig, loginRequest } from './AuthConfig'
import { captureException, setUser } from '@sentry/vue'

type AccountIdentifiers = Partial<Pick<AccountInfo, 'homeAccountId'|'localAccountId'|'username'>>;

export const msalInstance = new PublicClientApplication(msalConfig)

export interface AuthState {
  instance: PublicClientApplication | undefined,
  preferredLanguage: string | undefined | unknown,
  inProgress: InteractionStatus,
  accounts: Array<AccountInfo>,
  account: AccountInfo | undefined,
  isAuthenticated: boolean,
  error: EventError,
  idToken: string | undefined,
  idTokenExp: number | undefined,
  groups: string[] | undefined
}

/**
 * Helper function to determine whether 2 arrays are equal
 * Used to avoid unnecessary state updates
 * @param arrayA
 * @param arrayB
 */
function accountArraysAreEqual (arrayA: Array<AccountIdentifiers>, arrayB: Array<AccountIdentifiers>): boolean {
  if (arrayA.length !== arrayB.length) {
    return false
  }

  const comparisonArray = [...arrayB]

  return arrayA.every((elementA) => {
    const elementB = comparisonArray.shift()
    if (!elementA || !elementB) {
      return false
    }

    return (elementA.homeAccountId === elementB.homeAccountId) &&
           (elementA.localAccountId === elementB.localAccountId) &&
           (elementA.username === elementB.username)
  })
}

export const state:AuthState = {
  instance: msalInstance,
  accounts: [],
  account: undefined,
  inProgress: InteractionStatus.Startup,
  preferredLanguage: undefined,
  isAuthenticated: false,
  error: null,
  idToken: undefined,
  idTokenExp: undefined,
  groups: undefined
}

function setAccount () {
  state.account = msalInstance.getActiveAccount() || undefined
  setUser({
    id: state.account?.localAccountId,
    username: state.account?.username
  })
}

function setLang () {
  state.preferredLanguage = state.account?.idTokenClaims?.xms_tpl
}

msalInstance.addEventCallback((event: EventMessage) => {
  let currentAccounts
  let payload
  let error
  switch (event.eventType) {
    case EventType.INITIALIZE_START:
      currentAccounts = msalInstance.getAllAccounts()
      if (!accountArraysAreEqual(currentAccounts, state.accounts)) {
        state.accounts = currentAccounts
      }
      break
    case EventType.INITIALIZE_END:
      if (state.accounts.length === 1) {
        msalInstance.setActiveAccount(state.accounts[0])
        setAccount()
        setLang()
      }
      break
    case EventType.LOGIN_SUCCESS:
    case EventType.SSO_SILENT_SUCCESS:
      payload = event.payload as AuthenticationResult
      // eslint-disable-next-line no-console
      console.log('======================================================')
      // eslint-disable-next-line no-console
      console.log('EventType.LOGIN_SUCCESS || EventType.SSO_SILENT_SUCCESS: ', event.eventType)
      // eslint-disable-next-line no-console
      console.log(JSON.stringify(payload, null, 2))
      // eslint-disable-next-line no-console
      console.log('======================================================')
      msalInstance.setActiveAccount(payload.account)
      state.idToken = payload.idToken
      state.idTokenExp = payload.idTokenClaims.exp
      state.isAuthenticated = true
      state.error = null
      state.groups = payload.idTokenClaims.groups
      setAccount()
      setLang()
      break
    case EventType.ACQUIRE_TOKEN_SUCCESS:
      payload = event.payload as AuthenticationResult
      // eslint-disable-next-line no-console
      console.log('======================================================')
      // eslint-disable-next-line no-console
      console.log('EventType.ACQUIRE_TOKEN_SUCCESS')
      // eslint-disable-next-line no-console
      console.log(payload)
      // eslint-disable-next-line no-console
      console.log('======================================================')
      state.idToken = payload.idToken
      state.idTokenExp = payload.idTokenClaims.exp
      state.error = null
      state.groups = payload.idTokenClaims.groups
      break
    case EventType.LOGIN_FAILURE:
    case EventType.ACQUIRE_TOKEN_FAILURE:
    case EventType.SSO_SILENT_FAILURE:
    case EventType.ACQUIRE_TOKEN_BY_CODE_FAILURE:
      error = event.error
      // eslint-disable-next-line no-console
      console.error(error)
      if (!(error instanceof InteractionRequiredAuthError)) {
        captureException(error)
        state.error = event.error
      }
      break
    default:
      break
  }

  const inProgress = EventMessageUtils.getInteractionStatusFromEvent(event, state.inProgress)
  if (inProgress !== null) {
    state.inProgress = inProgress
  }
})

async function loginRedirect () {
  try {
    await msalInstance.loginRedirect({
      ...loginRequest
    })
    return true
  } catch (error) {
    return false
  }
}

async function acquireTokenRedirect () {
  try {
    await msalInstance.acquireTokenRedirect({
      ...loginRequest
    })
    return true
  } catch (error) {
    return false
  }
}

async function refreshIdToken () {
  try {
    await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: state.account,
      forceRefresh: true
    })
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      acquireTokenRedirect()
    } else {
      throw error
    }
  }
}

async function ssoSilent () {
  try {
    const hint = state.account?.idTokenClaims?.preferred_username || state.account?.username || undefined
    await msalInstance.ssoSilent({
      ...loginRequest,
      loginHint: hint,
      prompt: 'none'
    })
    return true
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError) {
      return loginRedirect()
    } else {
      return false
    }
  }
}

export async function isAuthenticated (): Promise<boolean> {
  if (state.account && state.idToken && !idTokenExpired()) {
    return true
  }

  if (state.account) {
    return await ssoSilent()
  }

  return loginRedirect()
}

function idTokenExpired (): boolean {
  const offset = Number(process.env.VUE_APP_MSAL_IDTOKEN_EXPIRE_OFFSET) || 0
  const exp = state.idTokenExp
  const now = Math.floor(Date.now() / 1000)
  return !!(exp && (exp - offset <= now))
}

export async function getIdToken () {
  if (idTokenExpired()) {
    await refreshIdToken()
  }
  return state.idToken
}

export async function getAccessToken (tokenRequest: SilentRequest, noRedirect = false) {
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...tokenRequest,
      account: state.account
    })
    return response.accessToken
  } catch (error) {
    if (error instanceof InteractionRequiredAuthError && !noRedirect) {
      acquireTokenRedirect()
    } else {
      throw error
    }
  }
}

export function logOut () {
  const logoutRequest = {
    account: state.account
  }

  msalInstance.logoutRedirect(logoutRequest)
}

await msalInstance.initialize()
await msalInstance.handleRedirectPromise()

export default {
  state,
  isAuthenticated,
  getIdToken,
  logOut
}
